import _extends from "@babel/runtime/helpers/extends";
import { Box } from '@wix/design-system';
import { RecommendationLabel } from '@wix/ambassador-premium-domains-v1-recommendation/types';
import { CoreDomainSearchStore, DomainSearchSuiteModule, SearchRecommendationModule } from '@wix/domains-search-suite';
import { Search } from '@wix/wix-ui-icons-common/odeditor';
import clsx from 'clsx';
import React, { useEffect } from 'react';
import { ResultsHeader } from '../../../Components/Headers/ResultsHeader/ResultsHeader';
import { Recommendations, RecommendationsError } from '../../../Components/Recommendations';
import { VoucherBanner } from '../../../Components/VoucherBanner';
import { useDomainSearchGateBi } from '../../../hooks/useDomainSearchGateBi';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { useIsMailboxReferralBM } from '../../../hooks/useIsMailboxReferral';
import { useResultsPageConfig } from '../../../hooks/useResultsPageConfig';
import { useGatePageBackground } from '../../../hooks/useGatePageBackground';
import { useIsBmHarmonyThemeEnabled } from '../../../utils/harmonyTheme';
import { HarmonyThemeProvider } from '@wix/core-domain-search';
import { useQueryParams } from '../../../hooks/useQueryParams';
import { useQuerySubmit } from '../../../hooks/useQuerySubmit';
import { useRecommendations } from '../../../hooks/useRecommendations';
import { useResultsBack } from '../../../hooks/useResultsBack';
import { useResultsPageOfferingsBi } from '../../../hooks/useResultsPageOfferingsBi';
import { useTldPrefill } from '../../../hooks/useTldPrefill';
import { DomainSearchGateModule } from '../../../module/domainSearchGate.module';
import { ExperimentSpecs } from '../../../constants';
import { DeclaredExperimentsProvider } from '../../../hooks/declaredExperiments';
import { usePerceptionEnabled, usePerceptionPricing, PERCEPTION_VOUCHER_BANNER } from '../../../pricingPerception';
import bannerVoucherStyles from '../../ResultsLayout/BannerVoucher.css';
import { ResultsLayoutDataHooks } from '../../ResultsLayout/dataHooks';
import { TldPrefillDescription } from '../../ResultsLayout/TldPrefillDescription';
import { useResultsSearchBarV4Layouts, V4_SEARCH_BAR_PLACEHOLDER_KEY } from '../../ResultsLayout/resultsSearchBarV4Layouts';
import s from '../../resultsPage.scss';
import transition from '../../viewTransitions.css';
export const SiteLevelDomainsResultsPage = _ref => {
  let {
    handlers,
    externalViewer
  } = _ref;
  const {
    getQueryParam
  } = useQueryParams();
  const autoInvokeOverride = getQueryParam('invoke') === 'true' ? true : undefined;
  // Declared here rather than in the content component: the pricing call happens inside it, and a
  // provider below the caller yields an empty id list, which reads to the server as "declared
  // nothing" and silently returns ungated pricing.
  const experimentIds = usePerceptionEnabled() ? [ExperimentSpecs.PricingPerceptionFirstYear] : [];
  const isHarmonyTheme = useIsBmHarmonyThemeEnabled();
  const {
    isV4
  } = useResultsPageConfig();
  return /*#__PURE__*/React.createElement(DeclaredExperimentsProvider, {
    ids: experimentIds
  }, /*#__PURE__*/React.createElement(DomainSearchSuiteModule.Provider, {
    translations: {
      Loading: /*#__PURE__*/React.createElement(React.Fragment, null)
    },
    actions: {
      siteInfo: {
        externalViewer: Boolean(externalViewer)
      },
      handlers,
      autoInvokeOverride,
      resolveCorrectedDomains: isV4
    }
  }, /*#__PURE__*/React.createElement(HarmonyThemeProvider, {
    enabled: isHarmonyTheme
  }, /*#__PURE__*/React.createElement(SiteLevelDomainsResultsPageContent, null))));
};
export const SiteLevelDomainsResultsPageContent = () => {
  DomainSearchGateModule.SDK.hooks.useDomainsSearchGateBM();
  const {
    t
  } = DomainSearchGateModule.internals.useTranslations();
  const {
    isV4
  } = useResultsPageConfig();
  // V4 gives BM the same bordered "table" list and single big hero as the storefront.
  const isOptimizedResultsExperience = isV4;
  const {
    heroLayouts,
    ...perceptionPricing
  } = usePerceptionPricing('businessManagerResults');
  const isMailboxReferral = useIsMailboxReferralBM();
  const pageBackground = useGatePageBackground();
  const isMobile = useIsMobile();
  const v4Layouts = useResultsSearchBarV4Layouts();
  const isV4Desktop = isV4 && !isMobile;
  const isV4Mobile = isV4 && isMobile;
  const voucherBannerConfigOverrides = perceptionPricing.PriceNote ? PERCEPTION_VOUCHER_BANNER : undefined;
  const handleBackClick = useResultsBack();
  const {
    getQueryParam
  } = useQueryParams();
  const {
    tld: prefillTld
  } = useTldPrefill();
  useDomainSearchGateBi({
    viewMode: 'Gate Result Page',
    prefilledTld: prefillTld,
    isEmptyState: Boolean(prefillTld) && !getQueryParam('q')
  });
  const {
    recommendations,
    isLoading,
    query,
    hasNextPage: hasMore,
    fetchNextPage,
    isFetchingNextPage,
    error,
    biError,
    onRetry,
    isPricingLoading
  } = useRecommendations();
  const {
    updateUrl
  } = DomainSearchSuiteModule.SDK.hooks.useUrlRecovery();
  useQuerySubmit({});
  useResultsPageOfferingsBi({
    recommendations,
    isLoading,
    biError
  });
  useEffect(() => {
    // Reset store when component unmounts
    return () => {
      CoreDomainSearchStore.resetStore();
    };
  }, []);
  const emptyQueryStatus = !isLoading && recommendations.length > 0 && !query;
  const tldPrefillEmptyState = Boolean(prefillTld) && !query;
  const searchBar = /*#__PURE__*/React.createElement(DomainSearchSuiteModule.SDK.Components.DomainSearchBar, {
    compact: true,
    showSearchIcon: isOptimizedResultsExperience,
    showOptionsIfEmptyInput: false,
    autoFocus: false,
    hideClearButton: isV4,
    hideFilters: tldPrefillEmptyState || isV4Desktop,
    useModalToolbar: isMobile,
    placeholder: isV4Desktop ? t(V4_SEARCH_BAR_PLACEHOLDER_KEY) : undefined,
    submitIcon: isV4Mobile ? /*#__PURE__*/React.createElement(Search, {
      color: "white"
    }) : undefined,
    onSubmit: filters => {
      updateUrl(filters);
    },
    onFiltersChange: (key, value) => {
      if (key === 'tlds') {
        DomainSearchSuiteModule.internals.bi.onSelectTld == null || DomainSearchSuiteModule.internals.bi.onSelectTld({
          tld: JSON.stringify(value)
        });
      } else {
        DomainSearchSuiteModule.internals.bi.onCTAClick == null || DomainSearchSuiteModule.internals.bi.onCTAClick({
          ctaClick: key,
          value: JSON.stringify(value)
        });
      }
    },
    Layouts: {
      ...(isV4Desktop && v4Layouts),
      StatusComponent: () => {
        if (tldPrefillEmptyState && prefillTld) {
          return /*#__PURE__*/React.createElement(TldPrefillDescription, {
            tld: prefillTld
          });
        }
        return emptyQueryStatus ? /*#__PURE__*/React.createElement(DomainSearchSuiteModule.SDK.Components.EmptyQueryStatus, null) : /*#__PURE__*/React.createElement(DomainSearchSuiteModule.SDK.Components.Status, null);
      }
    }
  });
  return /*#__PURE__*/React.createElement(Box, {
    align: "center",
    display: "grid",
    className: clsx(s.resultsPage, s.noInsights, isV4Desktop && bannerVoucherStyles.resultsScroller),
    gap: isV4 ? 'SP2 0' : 'SP6 0',
    height: "100%",
    backgroundColor: pageBackground,
    overflow: "auto"
  }, /*#__PURE__*/React.createElement(Box, {
    boxSizing: "border-box",
    align: "center",
    gridArea: "header",
    top: isMobile ? undefined : '0',
    position: isMobile ? undefined : 'sticky',
    width: "100%",
    zIndex: 10,
    padding: isMobile && isV4 ? 0 : isOptimizedResultsExperience ? 'SP5 48px 0 48px' : 'SP5 48px',
    backgroundColor: pageBackground,
    direction: "vertical",
    gap: "SP6"
  }, !isV4Desktop && !(isMobile && isV4) && /*#__PURE__*/React.createElement(VoucherBanner, {
    configOverrides: voucherBannerConfigOverrides
  }), !isV4 && /*#__PURE__*/React.createElement(ResultsHeader, {
    className: transition.header,
    onBackClick: handleBackClick,
    isMailboxReferral: isMailboxReferral,
    optimizedTitleCopy: isOptimizedResultsExperience,
    offsetTitle: tldPrefillEmptyState,
    topAligned: !!prefillTld
  }), !isMobile && /*#__PURE__*/React.createElement("div", {
    style: {
      width: '100%',
      zIndex: 11
    },
    className: transition.searchBar
  }, searchBar, isV4Desktop && /*#__PURE__*/React.createElement(Box, {
    dataHook: ResultsLayoutDataHooks.voucherInHeader,
    maxWidth: "980px",
    margin: "0 auto",
    padding: "12px 0",
    className: clsx(bannerVoucherStyles.voucherInHeader, bannerVoucherStyles.voucherInHeaderSiteLevel)
  }, /*#__PURE__*/React.createElement(VoucherBanner, {
    configOverrides: voucherBannerConfigOverrides
  })))), isMobile && /*#__PURE__*/React.createElement(Box, {
    gridArea: "searchbar",
    boxSizing: "border-box",
    position: "sticky",
    top: "0",
    zIndex: 11,
    backgroundColor: pageBackground,
    direction: "vertical",
    padding: "0 SP5",
    width: "100%",
    gap: "20px"
  }, isV4 && /*#__PURE__*/React.createElement(VoucherBanner, {
    configOverrides: voucherBannerConfigOverrides
  }), /*#__PURE__*/React.createElement("div", {
    className: transition.searchBar
  }, searchBar)), tldPrefillEmptyState ? null : error ? /*#__PURE__*/React.createElement(RecommendationsError, {
    error: error,
    onRetry: onRetry,
    gridColumn: "1 / -1"
  }) :
  /*#__PURE__*/
  // Two channels, because the hero and main cards are siblings of the list rather than
  // items in it: the prop reaches the rows, the provider reaches the cards.
  React.createElement(SearchRecommendationModule.Provider, {
    Layouts: {
      customConfiguration: heroLayouts ?? {}
    }
  }, /*#__PURE__*/React.createElement(Recommendations, _extends({
    recommendations: recommendations,
    isLoading: isLoading,
    hasMore: hasMore,
    isPricingLoading: isPricingLoading,
    fetchNextPage: () => {
      DomainSearchSuiteModule.internals.bi.onCTAClick == null || DomainSearchSuiteModule.internals.bi.onCTAClick({
        ctaClick: 'show_more_results'
      });
      fetchNextPage();
    },
    isFetchingNextPage: isFetchingNextPage,
    gridArea: "content",
    className: clsx(s.centeredContent, isV4 && s.narrowContent),
    useCompactRecommendationsLayout: isOptimizedResultsExperience,
    showHeroCard: isV4,
    promotedLabels: isV4 ? [RecommendationLabel.EXACT_MATCH, RecommendationLabel.GREAT_ALTERNATIVE] : undefined,
    suppressHarmonyCardStyling: isV4,
    desktopContentGap: isV4 ? 'SP4' : undefined,
    insightsPaddingTop: isV4 ? 'SP4' : undefined,
    compactPriceLabel: isV4,
    banner: isV4Desktop ? /*#__PURE__*/React.createElement(VoucherBanner, {
      configOverrides: voucherBannerConfigOverrides
    }) : undefined
  }, perceptionPricing))));
};
//# sourceMappingURL=SiteLevelDomainsResultsPage.js.map